/**
 * Rods, bait and lures.
 *
 * Two independent axes: the rod decides what you *can* land, the bait decides
 * what is interested. Keeping them separate is what makes tackle a decision —
 * a better rod alone opens nothing new in a pond if you are still offering
 * worms, and the right lure is wasted on a rod that cannot hold the fish.
 */

export type RodId = 'bambooRod' | 'fiberglassRod' | 'steelRod' | 'masterRod';

export interface RodDefinition {
  readonly id: RodId;
  readonly name: string;
  /** Highest fish tier this rod can land. */
  readonly tier: number;
  readonly description: string;
}

export const RODS: Readonly<Record<RodId, RodDefinition>> = {
  bambooRod: {
    id: 'bambooRod',
    name: 'Bamboo Rod',
    tier: 1,
    description: 'Cut from the reeds behind the cottage. Fine for panfish.',
  },
  fiberglassRod: {
    id: 'fiberglassRod',
    name: 'Fiberglass Rod',
    tier: 2,
    description: 'Springy enough for trout and bass.',
  },
  steelRod: {
    id: 'steelRod',
    name: 'Steel Rod',
    tier: 3,
    description: 'Will not fold on a pike or a big cat.',
  },
  masterRod: {
    id: 'masterRod',
    name: 'Master Rod',
    tier: 4,
    description: 'Holds anything that swims. Anything.',
  },
};

export const ROD_IDS = Object.keys(RODS) as RodId[];

export function isRodId(value: unknown): value is RodId {
  return typeof value === 'string' && value in RODS;
}

export type BaitId =
  | 'worm'
  | 'grub'
  | 'bread'
  | 'shrimp'
  | 'spinner'
  | 'flyLure'
  | 'spoonLure'
  | 'glowLure';

export interface BaitDefinition {
  readonly id: BaitId;
  readonly name: string;
  /** Lures are reusable; bait is eaten. */
  readonly reusable: boolean;
  readonly tint: string;
  readonly description: string;
}

export const BAITS: Readonly<Record<BaitId, BaitDefinition>> = {
  worm: {
    id: 'worm',
    name: 'Worm',
    reusable: false,
    tint: '#c9705f',
    description: 'Dug out of the soil. Almost anything will take one.',
  },
  grub: {
    id: 'grub',
    name: 'Grub',
    reusable: false,
    tint: '#e8dfc8',
    description: 'Turned up while hoeing. Small fish love them.',
  },
  bread: {
    id: 'bread',
    name: 'Bread',
    reusable: false,
    tint: '#e0b06a',
    description: 'Carp are not proud.',
  },
  shrimp: {
    id: 'shrimp',
    name: 'Shrimp',
    reusable: false,
    tint: '#f2a08a',
    description: 'Saltwater fish come a long way for these.',
  },
  // Lures survive the cast, which is what justifies the crafting cost.
  spinner: {
    id: 'spinner',
    name: 'Spinner',
    reusable: true,
    tint: '#c0c8d0',
    description: 'Flashes in the water. Predators cannot leave it alone.',
  },
  flyLure: {
    id: 'flyLure',
    name: 'Fly',
    reusable: true,
    tint: '#8fd6a0',
    description: 'Sits on the surface. Trout only.',
  },
  spoonLure: {
    id: 'spoonLure',
    name: 'Spoon',
    reusable: true,
    tint: '#f2c66d',
    description: 'Heavy, deep, and irresistible to big saltwater fish.',
  },
  glowLure: {
    id: 'glowLure',
    name: 'Glow Lure',
    reusable: true,
    tint: '#c79bea',
    description: 'The only thing that shows up in water this dark.',
  },
};

export const BAIT_IDS = Object.keys(BAITS) as BaitId[];

export function isBaitId(value: unknown): value is BaitId {
  return typeof value === 'string' && value in BAITS;
}

/** Raw materials, gathered rather than bought. */
export type MaterialId =
  | 'fiber'
  | 'driftwood'
  | 'shell'
  | 'pearl'
  | 'feather'
  | 'mothDust'
  | 'blightSap';

export interface MaterialDefinition {
  readonly id: MaterialId;
  readonly name: string;
  readonly tint: string;
  readonly source: string;
}

export const MATERIALS: Readonly<Record<MaterialId, MaterialDefinition>> = {
  fiber: { id: 'fiber', name: 'Fiber', tint: '#8fd6a0', source: 'Hoeing grass' },
  driftwood: { id: 'driftwood', name: 'Driftwood', tint: '#a8814f', source: 'Snagged while fishing' },
  shell: { id: 'shell', name: 'Shell', tint: '#e9e2d6', source: 'Digging wet sand' },
  pearl: { id: 'pearl', name: 'Pearl', tint: '#dff0f5', source: 'Rarely, in the sand' },
  feather: { id: 'feather', name: 'Feather', tint: '#f6f4e8', source: 'Left by the chickens' },

  // Dropped by what lives underground. Everything else in this table comes
  // from farming or fishing, so a forge that wants these is a forge that
  // wants you to have fought for them.
  mothDust: { id: 'mothDust', name: 'Moth Dust', tint: '#c79bea', source: 'Blight moths' },
  blightSap: { id: 'blightSap', name: 'Blight Sap', tint: '#4e8f4a', source: 'Root grubs' },
};

export const MATERIAL_IDS = Object.keys(MATERIALS) as MaterialId[];

export function isMaterialId(value: unknown): value is MaterialId {
  return typeof value === 'string' && value in MATERIALS;
}
